import React, { useMemo } from "react";

const ORANGE = "#f59e0b";
const GREEN = "#22c55e";
const RED = "#ef4444";

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeMinus(value) {
  return String(value).replace(/-/g, "−");
}

function uniqueNumbers(values, min, max) {
  return [...new Set(values.filter((value) => Number.isFinite(value) && value > min && value < max))]
    .sort((a, b) => a - b);
}

export default function NumberLine({
  min,
  max,
  selected = null,
  onChange,
  locked = false,
  validation = null,
  labelTicks,
  showSelectedLabel = false,
  hideSelectedTickLabel = false,
  width = 420,
  height = 88,
  maxWidth = 430,
  color = ORANGE,
  axisColor = "currentColor",
  fontFamily = "KaTeX_Main, STIXGeneral, 'Times New Roman', serif",
}) {
  const AR = 10;
  const pinR = 7;
  const strokeW = 3;
  const tickLength = 6;
  const leftX = 24;
  const rightX = width - 24;
  const axisY = 54;
  const pinTopY = axisY - 26;
  const selectedPoint = typeof selected === "number" ? selected : null;
  const isChecked = !!validation;
  const selectedCorrect =
    isChecked && selectedPoint !== null && selectedPoint === validation.correctValue;

  const ticks = useMemo(() => {
    const out = [];
    for (let value = min + 1; value <= max - 1; value += 1) out.push(value);
    return out;
  }, [min, max]);

  const labels = useMemo(() => {
    if (Array.isArray(labelTicks)) return uniqueNumbers(labelTicks, min, max);
    if (min < 0 && max > 1) return [0, 1];
    const mid = Math.round((min + max) / 2);
    return uniqueNumbers([min + 1, mid, max - 1], min, max);
  }, [labelTicks, min, max]);
  const visibleLabels =
    hideSelectedTickLabel && selectedPoint !== null
      ? labels.filter((label) => label !== selectedPoint)
      : labels;

  const xToPx = (value) => leftX + ((value - min) / (max - min)) * (rightX - leftX);

  const valueFromEvent = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const localX = ((event.clientX - rect.left) / rect.width) * width;
    const raw = min + ((localX - leftX) / (rightX - leftX)) * (max - min);
    return clamp(Math.round(raw), min + 1, max - 1);
  };

  const handleClick = (event) => {
    if (locked || !onChange) return;
    onChange(valueFromEvent(event));
  };

  const renderMarker = (value, markerColor, label, yOffset = 0) => {
    const x = xToPx(value);
    const yTop = pinTopY + yOffset;
    return (
      <g key={`${label}-${value}-${markerColor}-${yOffset}`}>
        <line
          x1={x}
          y1={axisY}
          x2={x}
          y2={yTop + pinR}
          stroke={markerColor}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />
        <circle cx={x} cy={yTop} r={pinR} fill={markerColor} />
        {label ? (
          <text
            x={x}
            y={yTop - 11}
            textAnchor="middle"
            dominantBaseline="baseline"
            fontSize="16"
            fontFamily={fontFamily}
            fontWeight="700"
            fill={markerColor}
            style={{ pointerEvents: "none" }}
          >
            {normalizeMinus(label)}
          </text>
        ) : null}
      </g>
    );
  };

  return (
    <div style={{ width: "100%", maxWidth }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role={onChange && !locked ? "button" : "img"}
        aria-label="oś liczbowa"
        onClick={handleClick}
        style={{
          display: "block",
          cursor: onChange && !locked ? "crosshair" : "default",
          overflow: "visible",
        }}
      >
        <line
          x1={leftX}
          y1={axisY}
          x2={rightX}
          y2={axisY}
          stroke={axisColor}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d={`M ${rightX - AR} ${axisY - AR / 2} L ${rightX} ${axisY} L ${rightX - AR} ${axisY + AR / 2} Z`}
          fill={axisColor}
        />

        {ticks.map((tick) => {
          const x = xToPx(tick);
          const isLabeled = visibleLabels.includes(tick);
          return (
            <g key={tick}>
              <line
                x1={x}
                y1={axisY - (isLabeled ? tickLength + 1 : tickLength)}
                x2={x}
                y2={axisY + (isLabeled ? tickLength + 1 : tickLength)}
                stroke={axisColor}
                strokeWidth={isLabeled ? "2" : "1.6"}
                opacity={isLabeled ? 0.95 : 0.62}
              />
              {isLabeled ? (
                <text
                  x={x}
                  y={axisY + 23}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="15"
                  fontFamily={fontFamily}
                  fill={axisColor}
                  style={{ pointerEvents: "none" }}
                >
                  {normalizeMinus(tick)}
                </text>
              ) : null}
            </g>
          );
        })}

        {!isChecked && selectedPoint !== null
          ? renderMarker(selectedPoint, color, showSelectedLabel ? String(selectedPoint) : "")
          : null}

        {isChecked && selectedPoint !== null && !selectedCorrect
          ? renderMarker(selectedPoint, RED, String(selectedPoint), 0)
          : null}
        {isChecked
          ? renderMarker(validation.correctValue, GREEN, String(validation.correctValue), selectedCorrect ? 0 : -8)
          : null}
      </svg>
    </div>
  );
}
